import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useEquipes } from '../context/EquipesContext';
import '../assets/css/MeuPerfil.css';

// O que a pessoa mantém sozinha: perfil, dados pessoais (com o CRECI) e contatos — as mesmas
// seções da ficha do RH. Permissão, equipe, username, vínculo e financeiro continuam com quem
// administra. A API valida de novo — o que está aqui é para poupar a ida ao servidor.
//
// A tela só abre com a senha (`/auth/perfil/desbloquear`): a sessão dura dias, e quem pega o
// computador aberto não deve ver o CPF nem trocar e-mail/telefone. O desbloqueio (30 min) fica
// só na memória desta tela — recarregar a página pede a senha de novo.
const ESTADOS_CIVIS = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União estável'];

const ICONES = {
  perfil: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-7 8a7 7 0 0 1 14 0',
  pessoais: 'M4 6h16v12H4zM8 10h4M8 14h6M16 10h.01',
  contatos: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z',
};

const SECOES = [
  {
    id: 'perfil', titulo: 'Perfil', descricao: 'Como a equipe e os clientes veem você.',
    campos: [
      { name: 'nome', label: 'Nome completo', placeholder: 'Como você aparece nos relatórios', largo: true },
      { name: 'email', label: 'E-mail', type: 'email', placeholder: 'seunome@61imoveis.com' },
      { name: 'telefone', label: 'Telefone com DDD', type: 'tel', placeholder: '(61) 99999-9999' },
      { name: 'instagram', label: 'Instagram', placeholder: 'seuperfil', prefixo: '@' },
      { name: 'descricao', label: 'Sobre você', type: 'textarea', maxLength: 500, largo: true,
        placeholder: 'Bairros em que atua, especialidade, idiomas…' },
    ],
  },
  {
    id: 'pessoais', titulo: 'Dados pessoais', descricao: 'Usados pelo RH no seu cadastro. Só você e o RH veem.',
    campos: [
      { name: 'creci', label: 'CRECI', placeholder: '12345-F' },
      { name: 'validade_creci', label: 'Validade do CRECI', type: 'date' },
      { name: 'cpf', label: 'CPF', placeholder: '000.000.000-00', inputMode: 'numeric', mascara: 'cpf' },
      { name: 'rg', label: 'RG', placeholder: 'Número e órgão emissor' },
      { name: 'data_nascimento', label: 'Data de nascimento', type: 'date' },
      { name: 'estado_civil', label: 'Estado civil', type: 'select', options: ESTADOS_CIVIS },
      { name: 'possui_filhos', label: 'Possui filhos', type: 'simnao' },
      { name: 'endereco', label: 'Endereço', type: 'textarea', maxLength: 500, largo: true,
        placeholder: 'Rua/quadra, número, bairro, cidade e CEP' },
    ],
  },
  {
    id: 'contatos', titulo: 'Contatos', descricao: 'Outros números e e-mails, e quem avisar numa emergência.',
    campos: [
      { name: 'telefone_corporativo', label: 'Telefone corporativo', type: 'tel', placeholder: '(61) 3333-4444' },
      { name: 'email_corporativo', label: 'E-mail corporativo', type: 'email' },
      { name: 'telefone_pessoal', label: 'Telefone pessoal', type: 'tel', placeholder: '(61) 99999-9999' },
      { name: 'email_pessoal', label: 'E-mail pessoal', type: 'email' },
      { name: 'contato_emergencia', label: 'Contato de emergência', type: 'textarea', maxLength: 500, largo: true,
        placeholder: 'Nome, parentesco e telefone' },
    ],
  },
];
const CAMPOS = SECOES.flatMap((s) => s.campos);
const CAMPOS_RH = SECOES.filter((s) => s.id !== 'perfil').flatMap((s) => s.campos);

function mascaraCpf(valor) {
  const d = String(valor || '').replace(/\D/g, '').slice(0, 11);
  return d.replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

function paraForm(dados) {
  return CAMPOS.reduce((acc, c) => {
    const v = dados?.[c.name];
    acc[c.name] = c.type === 'simnao' ? (v === true ? 'true' : v === false ? 'false' : '') : (v ?? '');
    return acc;
  }, {});
}

const iniciais = (nome) => String(nome || '').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || 'U';

function Icone({ d, tamanho = 18 }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
  );
}

/** Pede a senha. Usada como tela inteira (abrir) e como janela (desbloqueio venceu ao salvar). */
function ConfirmarSenha({ titulo, texto, botao, nome, onAbrir, onCancelar, janela = false }) {
  const [senha, setSenha] = useState('');
  const [ver, setVer] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const campo = useRef(null);

  useEffect(() => { campo.current?.focus(); }, []);

  async function enviar(e) {
    e.preventDefault();
    if (!senha) { setErro('Digite sua senha.'); campo.current?.focus(); return; }
    setEnviando(true); setErro('');
    try {
      const r = await fetch(`${BASE}/auth/perfil/desbloquear`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ senha }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErro(d.error || 'Não foi possível confirmar sua senha.');
        setSenha(''); campo.current?.focus();
        return;
      }
      await onAbrir(d);
    } catch {
      setErro('Não foi possível conectar ao servidor.');
    } finally {
      setEnviando(false);
    }
  }

  const cartao = (
    <form className={`mp-cofre ${janela ? 'mp-cofre--janela' : ''}`} onSubmit={enviar}
      role={janela ? 'dialog' : undefined} aria-modal={janela || undefined} aria-labelledby="mp-cofre-titulo">
      <span className="mp-cofre-icone" aria-hidden="true">
        <Icone d="M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5zM12 15v2" tamanho={26} />
      </span>
      {nome && <p className="mp-cofre-ola">Olá, {String(nome).split(/\s+/)[0]}</p>}
      <h2 id="mp-cofre-titulo">{titulo}</h2>
      <p className="mp-cofre-texto">{texto}</p>
      <label className="ds-label" htmlFor="mp-senha">Sua senha</label>
      <div className="mp-senha-campo">
        <input id="mp-senha" ref={campo} className="ds-input" type={ver ? 'text' : 'password'} autoComplete="current-password"
          value={senha} disabled={enviando} placeholder="A mesma que você usa para entrar"
          onChange={(e) => { setSenha(e.target.value); setErro(''); }} />
        <button type="button" className="mp-senha-ver" onClick={() => setVer((v) => !v)}
          aria-label={ver ? 'Esconder senha' : 'Mostrar senha'}>{ver ? 'Esconder' : 'Mostrar'}</button>
      </div>
      {erro && <div className="ds-alert ds-alert-error mp-cofre-erro" role="alert">{erro}</div>}
      <button type="submit" className="ds-btn ds-btn-primary ds-btn-full" disabled={enviando}>
        {enviando ? <><span className="ds-spinner" /> Conferindo…</> : botao}
      </button>
      {onCancelar && <button type="button" className="mp-link" onClick={onCancelar} disabled={enviando}>Cancelar</button>}
      <small className="mp-cofre-dica">Esqueceu a senha? Peça ao administrativo para redefinir.</small>
    </form>
  );
  return janela ? <div className="mp-janela-fundo">{cartao}</div> : <div className="mp-cofre-pagina">{cartao}</div>;
}

export default function MeuPerfil() {
  const { isLogado, userData, login } = useAuth();
  const { getNomeEquipe } = useEquipes();
  const navigate = useNavigate();
  const [desbloqueio, setDesbloqueio] = useState(null);       // { token, expiraEm }
  const [reconfirmar, setReconfirmar] = useState(false);     // venceu com edição na tela
  const [dados, setDados] = useState(null);
  const [base, setBase] = useState(() => paraForm({}));
  const [form, setForm] = useState(() => paraForm({}));
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');
  const [agora, setAgora] = useState(() => Date.now());
  const salvarDepois = useRef(false);

  const alterados = useMemo(() => CAMPOS.filter((c) => String(form[c.name] ?? '') !== String(base[c.name] ?? '')),
    [form, base]);
  const progresso = useMemo(() => {
    const feitos = CAMPOS_RH.filter((c) => String(base[c.name] ?? '').trim()).length;
    return { feitos, total: CAMPOS_RH.length, pct: Math.round((100 * feitos) / CAMPOS_RH.length) };
  }, [base]);

  // Relógio do desbloqueio: ao vencer, sem edição pendente a tela volta para a senha.
  useEffect(() => {
    if (!desbloqueio) return undefined;
    const id = setInterval(() => setAgora(Date.now()), 15000);
    return () => clearInterval(id);
  }, [desbloqueio]);
  const restanteMin = desbloqueio ? Math.max(0, Math.ceil((desbloqueio.expiraEm - agora) / 60000)) : 0;
  useEffect(() => {
    if (desbloqueio && restanteMin <= 0) {
      if (alterados.length) setReconfirmar(true);
      else { setDesbloqueio(null); setDados(null); }
    }
  }, [desbloqueio, restanteMin, alterados.length]);

  const aplicarDados = useCallback((novos, manterEdicao = false) => {
    const f = paraForm(novos);
    setDados(novos);
    setBase(f);
    if (!manterEdicao) setForm(f);
  }, []);

  const abrir = useCallback(async (d) => {
    setDesbloqueio({ token: d.desbloqueio, expiraEm: Number(d.expira_em) * 1000 });
    setAgora(Date.now());
    aplicarDados(d.dados || {}, reconfirmar);
    setReconfirmar(false);
  }, [aplicarDados, reconfirmar]);

  const salvar = useCallback(async (e) => {
    e?.preventDefault();
    setMensagem(''); setErro('');
    if (!String(form.nome || '').trim()) { setErro('Informe seu nome completo.'); return; }
    if (!alterados.length) return;
    setLoading(true);
    try {
      // Só o que mudou: um valor antigo fora do padrão (cadastrado pelo RH) não trava o resto.
      const corpo = Object.fromEntries(alterados.map((c) => [c.name, form[c.name]]));
      const resposta = await fetch(`${BASE}/auth/perfil`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...corpo, desbloqueio: desbloqueio?.token }),
      });
      const data = await resposta.json().catch(() => ({}));
      if (!resposta.ok) {
        if (data.code === 'desbloqueio') { salvarDepois.current = true; setReconfirmar(true); return; }
        setErro(data.error || 'Não foi possível salvar seus dados.');
        return;
      }
      // Mantém a sessão: o token atual continua valendo, só os dados exibidos mudam.
      login(data.user, localStorage.getItem('auth_token'));
      aplicarDados(data.dados || { ...dados, ...form });
      setMensagem(data.ok || 'Dados atualizados.');
    } catch {
      setErro('Não foi possível conectar ao servidor.');
    } finally {
      setLoading(false);
    }
  }, [alterados, form, desbloqueio, login, aplicarDados, dados]);

  // Senha reconfirmada depois de um salvar recusado: salva o que estava na tela.
  useEffect(() => {
    if (desbloqueio && !reconfirmar && salvarDepois.current) { salvarDepois.current = false; salvar(); }
  }, [desbloqueio, reconfirmar, salvar]);

  if (!isLogado) {
    return <div className="ds-auth-wrapper"><div className="ds-auth-card">
      <h2 className="ds-auth-title">Meus dados</h2>
      <p className="ds-auth-subtitle">Entre para ver e alterar os dados da sua conta.</p>
      <button className="ds-btn ds-btn-primary ds-btn-full" onClick={() => navigate('/login')}>Entrar</button>
    </div></div>;
  }

  if (!desbloqueio || !dados) {
    return (
      <ConfirmarSenha nome={userData.nome} titulo="Meus dados" botao="Abrir meus dados" onAbrir={abrir}
        texto="Por segurança, confirme sua senha para ver e editar seus dados pessoais." />
    );
  }

  function alterar(campo, valor) {
    setMensagem('');
    setForm((atual) => ({ ...atual, [campo]: valor }));
  }

  function campo(c) {
    const id = `perfil-${c.name}`;
    const mudou = String(form[c.name] ?? '') !== String(base[c.name] ?? '');
    const comum = { id, className: 'ds-input', value: form[c.name] ?? '', disabled: loading };
    let controle;
    if (c.type === 'textarea') {
      controle = <textarea {...comum} rows={3} maxLength={c.maxLength} placeholder={c.placeholder}
        onChange={(e) => alterar(c.name, e.target.value)} />;
    } else if (c.type === 'select' || c.type === 'simnao') {
      const opcoes = c.type === 'simnao' ? [['true', 'Sim'], ['false', 'Não']] : c.options.map((o) => [o, o]);
      controle = <select {...comum} onChange={(e) => alterar(c.name, e.target.value)}>
        <option value="">Selecione</option>
        {opcoes.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
      </select>;
    } else {
      controle = <input {...comum} type={c.type || 'text'} placeholder={c.placeholder} inputMode={c.inputMode}
        onChange={(e) => alterar(c.name, c.mascara === 'cpf' ? mascaraCpf(e.target.value) : e.target.value)} />;
    }
    return (
      <div className={`mp-campo ${c.largo ? 'mp-largo' : ''} ${mudou ? 'is-alterado' : ''}`} key={c.name}>
        <label className="ds-label" htmlFor={id}>{c.label}{mudou && <span className="mp-alterado-tag">alterado</span>}</label>
        {c.prefixo ? <div className="mp-prefixo"><span aria-hidden="true">{c.prefixo}</span>{controle}</div> : controle}
      </div>
    );
  }

  const preenchidosNaSecao = (s) => s.campos.filter((c) => String(base[c.name] ?? '').trim()).length;
  const equipe = dados.team ? (getNomeEquipe(dados.team) || dados.team) : 'Sem equipe';

  return (
    <div className="mp">
      <header className="mp-hero">
        <div className="mp-hero-id">
          <span className="mp-avatar" aria-hidden="true">{iniciais(dados.nome || userData.nome)}</span>
          <div>
            <span className="mp-eyebrow">Meus dados</span>
            <h1>{dados.nome || userData.nome}</h1>
            <div className="mp-chips">
              <span className="mp-chip">{dados.permissao || 'corretor'}</span>
              <span className="mp-chip">{equipe}</span>
              <span className="mp-chip mp-chip--claro">ID {dados.id_usuarios || '—'}</span>
              <span className="mp-chip mp-chip--claro">@{dados.username || '—'}</span>
            </div>
          </div>
        </div>
        <div className="mp-hero-acoes">
          <span className="mp-cadeado" title="Depois disso a tela pede a senha de novo">
            <Icone d="M7 11V8a5 5 0 0 1 9.6-2M5 11h14v10H5z" tamanho={14} /> Aberto · fecha em {restanteMin} min
          </span>
          <button type="button" className="mp-hero-btn" onClick={() => navigate('/TrocarSenha')}>Trocar senha</button>
          <button type="button" className="mp-hero-btn" onClick={() => { setDesbloqueio(null); setDados(null); }}>Fechar</button>
        </div>
      </header>

      <div className="mp-corpo">
        <aside className="mp-lateral">
          <div className="mp-progresso" aria-label={`Cadastro ${progresso.pct}% completo`}>
            <div className="mp-anel" style={{ '--pct': progresso.pct }}><span>{progresso.pct}%</span></div>
            <div>
              <strong>Seu cadastro</strong>
              <small>{progresso.feitos} de {progresso.total} dados pessoais e contatos</small>
            </div>
          </div>
          <nav className="mp-indice" aria-label="Seções">
            {SECOES.map((s) => (
              <a key={s.id} href={`#mp-${s.id}`}>
                <Icone d={ICONES[s.id]} tamanho={16} />
                <span>{s.titulo}</span>
                <small>{preenchidosNaSecao(s)}/{s.campos.length}</small>
              </a>
            ))}
          </nav>
          <p className="mp-lateral-nota">
            Permissão, equipe e usuário são alterados pelo administrativo. Dados bancários, contrato e vínculo
            com a 61, pelo RH.
          </p>
        </aside>

        <form className="mp-form" onSubmit={salvar}>
          {SECOES.map((s) => (
            <section className="mp-secao" key={s.id} id={`mp-${s.id}`} aria-labelledby={`mp-${s.id}-titulo`}>
              <header>
                <span className="mp-secao-icone"><Icone d={ICONES[s.id]} /></span>
                <div>
                  <h2 id={`mp-${s.id}-titulo`}>{s.titulo}</h2>
                  <p>{s.descricao}</p>
                </div>
              </header>
              <div className="mp-grade">{s.campos.map(campo)}</div>
            </section>
          ))}

          {erro && <div className="ds-alert ds-alert-error" role="alert">{erro}</div>}
          {mensagem && <div className="ds-alert ds-alert-success" role="status">{mensagem}</div>}

          <div className={`mp-barra ${alterados.length ? 'is-visivel' : ''}`} aria-hidden={!alterados.length}>
            <span><b>{alterados.length}</b> {alterados.length === 1 ? 'alteração não salva' : 'alterações não salvas'}</span>
            <button type="button" className="mp-link" disabled={loading || !alterados.length}
              onClick={() => { setForm(base); setErro(''); }}>Descartar</button>
            <button type="submit" className="ds-btn ds-btn-primary" disabled={loading || !alterados.length}>
              {loading ? <><span className="ds-spinner" /> Salvando...</> : 'Salvar alterações'}
            </button>
          </div>
        </form>
      </div>

      {reconfirmar && (
        <ConfirmarSenha janela nome={null} titulo="Confirme sua senha" botao={salvarDepois.current ? 'Confirmar e salvar' : 'Continuar'}
          texto="A tela ficou aberta por 30 minutos. Confirme a senha para continuar — o que você digitou não se perde."
          onAbrir={abrir}
          onCancelar={() => { salvarDepois.current = false; setReconfirmar(false); setDesbloqueio(null); setDados(null); }} />
      )}
    </div>
  );
}
